import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Add24Regular, Delete24Regular, DeleteDismiss24Regular } from "@fluentui/react-icons";
import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from "@fluentui/react";

import { deleteAllChats, deleteChat, getChats } from "../../api/api";
import { ChatHistoryItem } from "../../api/models";

import styles from "./ChatHistorySidebar.module.css";

interface Props {
    onNewChat: () => void;
    onChatsChange?: (hasChats: boolean) => void;
}

export const ChatHistorySidebar = ({ onNewChat, onChatsChange }: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeChatId = searchParams.get("chatId");

    const [chats, setChats] = useState<ChatHistoryItem[]>([]);
    const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
    const [showClearAllDialog, setShowClearAllDialog] = useState(false);

    useEffect(() => {
        // Only the latest request may set the list: an earlier one can answer later.
        const controller = new AbortController();
        getChats(controller.signal).then(res => {
            if (controller.signal.aborted) return;
            const items = res.data ?? [];
            setChats(items);
            onChatsChange?.(items.length > 0);
        });

        return () => controller.abort();
    }, [activeChatId]);

    const navigateToChat = (chatId: string) => {
        const params = new URLSearchParams();
        params.set("chatId", chatId);
        setSearchParams(params);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingChatId) return;
        const wasActive = deletingChatId === activeChatId;
        await deleteChat(deletingChatId);
        setChats(prev => {
            const updated = prev.filter(c => c.chatId !== deletingChatId);
            onChatsChange?.(updated.length > 0);
            return updated;
        });
        setDeletingChatId(null);
        if (wasActive) onNewChat();
    };

    const handleClearAllConfirm = async () => {
        await deleteAllChats();
        setChats([]);
        onChatsChange?.(false);
        setShowClearAllDialog(false);
        onNewChat();
    };

    if (chats.length === 0) return null;

    return (
        <aside className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
                <button className={styles.newChatButton} onClick={onNewChat}>
                    <Add24Regular />
                    New Chat
                </button>
                <button
                    className={styles.clearAllButton}
                    onClick={() => setShowClearAllDialog(true)}
                    title="Clear all history"
                >
                    <DeleteDismiss24Regular />
                </button>
            </div>
            <nav className={styles.list}>
                {chats.map(chat => (
                    <div
                        key={chat.chatId}
                        className={`${styles.entryRow} ${chat.chatId === activeChatId ? styles.entryRowActive : ""}`}
                    >
                        <button
                            className={styles.entry}
                            onClick={() => navigateToChat(chat.chatId)}
                            title={chat.title}
                        >
                            <span className={styles.entryTitle}>{chat.title}</span>
                        </button>
                        <button
                            className={styles.deleteEntryButton}
                            onClick={() => setDeletingChatId(chat.chatId)}
                            title="Delete chat"
                        >
                            <Delete24Regular />
                        </button>
                    </div>
                ))}
            </nav>

            <Dialog
                hidden={!deletingChatId}
                onDismiss={() => setDeletingChatId(null)}
                modalProps={{ containerClassName: styles.dialogContainer }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Delete Chat",
                    subText: "Are you sure you want to delete this chat? This action cannot be undone.",
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleDeleteConfirm} text="Delete" className={styles.dialogDeleteButton} />
                    <DefaultButton onClick={() => setDeletingChatId(null)} text="Cancel" className={styles.dialogCancelButton} />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!showClearAllDialog}
                onDismiss={() => setShowClearAllDialog(false)}
                modalProps={{ containerClassName: styles.dialogContainer }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Clear All History",
                    subText: "Are you sure you want to delete all chat history? This action cannot be undone.",
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleClearAllConfirm} text="Clear All" className={styles.dialogDeleteButton} />
                    <DefaultButton onClick={() => setShowClearAllDialog(false)} text="Cancel" className={styles.dialogCancelButton} />
                </DialogFooter>
            </Dialog>
        </aside>
    );
};
